import React from "react";

import { ArrowRight, ShieldCheck } from "lucide-react";

import { useBrandingConfig } from "../../branding";
import { Checkbox } from "../../components/ui/checkbox";
import { NEEDL_LINKS } from "../../constants/links";
import { getIcon } from "../../utils/getIcon";
import {
  getAuthenticationDescription,
  getAuthenticationRestrictionText,
  getIntegrationLimitationNote,
} from "../utils";

import { integrationButton } from "./shared/integrationButton";
import { StepIndicator } from "./StepIndicator";

import type { ModalStepProps } from "../types/modal";

const BrandLogo = () => {
  const { logo } = useBrandingConfig();
  if (typeof logo === "string") {
    return <img src={logo} alt="" className="h-12 w-12 object-contain" />;
  }
  const Logo = logo;
  return <Logo className="h-12 w-12" />;
};

export function PolishedAuthenticateStep({
  integration,
  integrationScope,
  onConnect,
  onClose,
  isLoading,
}: ModalStepProps) {
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const { scAppName } = useBrandingConfig();
  const note = getIntegrationLimitationNote(integration.label);

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto">
      <div className="flex h-[136px] shrink-0 items-center justify-center gap-5 rounded-xl bg-primary/[0.06] shadow-[inset_0_0_0_0.5px_hsl(var(--foreground)/0.06)]">
        <BrandLogo />
        <span
          aria-hidden
          className="relative h-0.5 w-[88px] bg-[repeating-linear-gradient(90deg,hsl(var(--primary)/0.4)_0_4px,transparent_4px_8px)]"
        >
          <i className="absolute -top-[3px] left-0 h-2 w-2 rounded-full bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)] motion-safe:animate-[link-travel_1.8s_cubic-bezier(0.45,0,0.55,1)_infinite] motion-reduce:left-10" />
        </span>
        <span className="grid h-12 w-12 place-items-center">
          {getIcon(integration.label, 48)}
        </span>
      </div>

      <div className="mt-6">
        <StepIndicator
          currentStep="authenticate"
          sourceId={integration.label}
          integrationScope={integrationScope}
        />
      </div>

      <h2 className="mt-5 text-lg font-semibold text-foreground">
        Sign in to {integration.value}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {getAuthenticationDescription(integration.label)}. {scAppName} will not
        be able to write or modify any{" "}
        {getAuthenticationRestrictionText(integration.label)}.
      </p>

      {note && (
        <p className="mt-4 rounded-[10px] bg-primary/[0.06] px-3.5 py-3 text-[13px] leading-5 text-foreground/80">
          <span className="font-semibold text-foreground">Note:</span> {note}
        </p>
      )}

      <div className="mt-4 flex items-start gap-2">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
          className="mt-0.5"
        />
        <label
          htmlFor="terms"
          className="cursor-pointer text-sm text-muted-foreground"
        >
          I agree to the{" "}
          <a
            href={NEEDL_LINKS.TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline underline-offset-2 hover:no-underline"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={NEEDL_LINKS.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline underline-offset-2 hover:no-underline"
          >
            Privacy Policy
          </a>
          .
        </label>
      </div>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
        Read-only access. Revoke it any time from {integration.value}.
      </p>

      <div className="mt-7 flex items-center justify-end gap-2">
        <button
          type="button"
          className={integrationButton("neutral")}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className={integrationButton("primary")}
          onClick={() => onConnect?.()}
          disabled={!termsAccepted || isLoading}
          data-testid="integration-sign-in-button"
        >
          Continue to {integration.value}
          <ArrowRight />
        </button>
      </div>
    </div>
  );
}
